// src/modules/bookLocation/keyHandover/pages/SubscriptionChoicePage.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { SubscriptionChoiceProvider } from "../context/SubscriptionChoiceContext";
import SubscriptionChoiceStep from "../steps/KeyHandoverSubscriptionChoiceStep";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../hooks/useSubscriptionUrlSync";
import { Spinner } from "../../../../components/ui/spinner";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import routes from "../../../../routes/routes";
import type { RootState } from "../../../../redux/store";

function ChoiceContent() {
    const { t } = useTranslation();
    const hydratedRef = React.useRef(false);

    const locationHydrated = useLocationHydration();

    // Hydrate subscription state from URL
    useSubscriptionHydration();

    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);

    // Mark as hydrated after first render
    React.useEffect(() => {
        if (!hydratedRef.current) {
            hydratedRef.current = true;
        }
    }, []);

    if (!locationHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <SubscriptionChoiceProvider>
            <SubscriptionChoiceStep />
        </SubscriptionChoiceProvider>
    );
}

export const SubscriptionChoicePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const hasExistingSubscription = useSelector(
        (state: RootState) => state.subscription.hasExistingSubscription
    );

    const handleNext = () => {
        const location = window.location;
        if (hasExistingSubscription === "existing") {
            // Flow 3: Existing subscription → Login/Verify
            navigate({
                pathname: routes.keyHandoverSubscriptionLoginPath,
                search: location.search,
            });
        } else if (hasExistingSubscription === "new") {
            // Flow 2: New subscription → Plan Selection
            navigate({
                pathname: routes.keyHandoverSubscriptionPlanSelectionPath,
                search: location.search,
            });
        }
    };

    const bottomActions = (
        <KeyHandoverNavigateNextAction
            onNext={handleNext}
            enabled={Boolean(hasExistingSubscription)}
            bounceKey={hasExistingSubscription}
        />
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_choice.header_text")}
            backTo={routes.keyHandoverPaymentModelPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <ChoiceContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionChoicePage;
